import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import { ProjectObjectService } from '@Core/models/proprietary/project/project-object.service';
import { ProjectObjectState } from '@Core/models/proprietary/project/project-object.state';
import { EventType as ProjectEventType } from '@Core/models/proprietary/project/project-object.types';
import { TaskObjectService } from '@Core/models/proprietary/task/task-object.service';
import { TaskObjectState } from '@Core/models/proprietary/task/task-object.state';
import { EventType, TaskActionType, TaskObjectValidationEvents } from '@Core/models/proprietary/task/task-object.types';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataSourceStatus } from '@Core/services/proprietary/datasource';
import { TaskActionRules } from './task-action.rules';

@Component({
    standalone: false,
    selector: 'ep-task-action',
    templateUrl: './task-action.component.html'
})
export class TaskActionComponent implements OnInit, OnDestroy {
    dsTaskActionProcessSub: Subscription;
    dsTaskActionGetSub: Subscription;

    dsProjectActionProcessSub: Subscription;
    dsProjectActionGetSub: Subscription;

    saveTask: any;
    saveProject: any;
    _taskActionMode: TaskActionType;
    titleText: string = '';

    taskActionOptions = {
        titleText: '',
        buttonText: 'Close',
        event: <EventType>null,
        editTaskInfo: false,
        editNote: false,
        editRoute: false,
        editProjectInfo: false,
        pageIcon: '',
        buttonIcon: ''
    };

    @Input()
    set taskActionMode(mode: TaskActionType) {
        this._taskActionMode = mode;
        switch (mode) {
            case TaskActionType.MarkDone:
                this.taskActionOptions.titleText = 'Mark Task Done';
                this.taskActionOptions.editNote = true;
                this.taskActionOptions.buttonText = 'Mark Done';
                this.taskActionOptions.event = EventType.Complete;
                this.taskActionOptions.pageIcon = 'checkmark';
                this.taskActionOptions.buttonIcon = 'checkmark';
                break;

            case TaskActionType.Pend:
                this.taskActionOptions.titleText = 'Pend Task';
                this.taskActionOptions.editTaskInfo = true;
                this.taskActionOptions.editNote = true;
                this.taskActionOptions.buttonText = 'Save';
                this.taskActionOptions.event = EventType.Update;
                this.taskActionOptions.pageIcon = 'fa-ep-task-pend';
                this.taskActionOptions.buttonIcon = 'save';
                break;

            case TaskActionType.Route:
                this.taskActionOptions.titleText = 'Route Task';
                this.taskActionOptions.editTaskInfo = true;
                this.taskActionOptions.editNote = false;
                this.taskActionOptions.editRoute = true;
                this.taskActionOptions.buttonText = 'Route';
                this.taskActionOptions.event = EventType.Update;
                this.taskActionOptions.pageIcon = 'fa-ep-route';
                this.taskActionOptions.buttonIcon = 'fa-ep-route';
                break;

            case TaskActionType.Skip:
                this.taskActionOptions.titleText = 'Skip Task';
                this.taskActionOptions.editNote = true;
                this.taskActionOptions.buttonText = 'Skip Task';
                this.taskActionOptions.event = EventType.Skip;
                this.taskActionOptions.pageIcon = 'fa-ep-status-skipped';
                this.taskActionOptions.buttonIcon = 'fa-ep-status-skipped';
                break;

            case TaskActionType.EditNote:
                this.taskActionOptions.titleText = 'Task Note';
                this.taskActionOptions.editNote = true;
                this.taskActionOptions.buttonText = 'Save';
                this.taskActionOptions.event = EventType.Update;
                this.taskActionOptions.pageIcon = 'fa-ep-note-add';
                this.taskActionOptions.buttonIcon = 'save';
                break;

            case TaskActionType.EditProject:
                this.taskActionOptions.titleText = 'Edit Project';
                this.taskActionOptions.editProjectInfo = true;
                (this.taskActionOptions.buttonText = 'Save'), (this.taskActionOptions.event = EventType.Update);
                this.taskActionOptions.pageIcon = 'fa-ep-project';
                this.taskActionOptions.buttonIcon = 'save';
                break;

            case TaskActionType.History:
                this.taskActionOptions.titleText = 'Task History';
                this.taskActionOptions.pageIcon = 'fa-ep-history';
                break;
        }
    }

    constructor(
        public taskOS: TaskObjectService,
        public projectOS: ProjectObjectService,
        private store: Store,
        public activeModal: NgbActiveModal,
        public app: AppService,
        public pageOS: PageObjectService
    ) {
        this.pageOS.saveValidationRules();
        this.pageOS.addObjectRuleSets([TaskActionRules]);
        this.pageOS.setObjectValidationEvents(TaskObjectValidationEvents);
    }

    ngOnInit() {
        this.saveTask = this.store.selectSnapshot(TaskObjectState.task);
        this.saveProject = this.store.selectSnapshot(ProjectObjectState.project);
    }

    ngOnDestroy() {
        this.unsubscribeFromTask();
        this.unsubscribeFromProject();
    }

    actionTaken() {
        if (this.taskActionOptions.event) {
            if (this._taskActionMode != TaskActionType.EditProject) {
                //TODO: HACK! This should probably be a subscription to an observable in TaskObjectService 
                // rather than watching the datasource in AppService
                this.app.dsTaskProcess.reset();
                this.app.dsTaskGet.reset();
                this.dsTaskActionProcessSub = this.app.dsTaskProcess.status$.subscribe((x) => {
                    this.taskProcessStatusUpdated(x);
                });
                this.dsTaskActionGetSub = this.app.dsTaskGet.status$.subscribe((x) => {
                    this.taskGetStatusUpdated(x);
                });
                //END HACK

                this.taskOS.process(this.taskActionOptions.event, {}, true);
                let missingEvents = this.pageOS.getMissingEvents();
                if (this.taskActionOptions.event == EventType.Complete && missingEvents.length > 0) {
                    this.pageOS.restoreValidationRules();
                    this.activeModal.close();
                }
            } else {
                this.app.dsProjectProcess.reset();
                this.app.dsProjectGet.reset();

                this.projectOS.process(ProjectEventType.Update);

                this.dsProjectActionProcessSub = this.app.dsProjectProcess.status$.subscribe((x) => {
                    this.projectProcessStatusUpdated(x);
                });
                this.dsProjectActionGetSub = this.app.dsProjectGet.status$.subscribe((x) => {
                    this.projectGetStatusUpdated(x);
                });
                //END HACK
            }
        } else {
            this.activeModal.close();
        }
    }

    //fired after update complete
    taskProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Error:
                this.unsubscribeFromTask();
                break;
        }
    }

    //fired after update reloads the new task
    taskGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.unsubscribeFromTask();
                this.pageOS.restoreValidationRules();
                this.activeModal.close('done');
                break;
            case DataSourceStatus.Error:
                this.unsubscribeFromTask();
                break;
        }
    }

    //fired after update complete
    projectProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Error:
                this.unsubscribeFromProject();
                break;
        }
    }

    //fired after update reloads the project
    projectGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.unsubscribeFromProject();
                this.pageOS.restoreValidationRules();
                this.activeModal.close('done');
                break;
            case DataSourceStatus.Error:
                this.unsubscribeFromProject();
                break;
        }
    }

    unsubscribeFromProject() {
        if (this.dsProjectActionProcessSub) {
            this.dsProjectActionProcessSub.unsubscribe();
        }
        if (this.dsProjectActionGetSub) {
            this.dsProjectActionGetSub.unsubscribe();
        }
    }

    unsubscribeFromTask() {
        if (this.dsTaskActionProcessSub) {
            this.dsTaskActionProcessSub.unsubscribe();
        }
        if (this.dsTaskActionGetSub) {
            this.dsTaskActionGetSub.unsubscribe();
        }
    }

    cancel() {
        if (this._taskActionMode != TaskActionType.EditProject) {
            if (this.saveTask) {
                let restoreTask = { ...this.saveTask, Route: null }; //always clear route node on cancel
                restoreTask.Active_Date_Quick_Select = ''; //always clear
                restoreTask.Late_Date_Quick_Select = ''; //always clear
                this.taskOS.updateTask(restoreTask); //restore task
            }
        } else {
            if (this.saveProject) {
                this.projectOS.updateProject(this.saveProject);
            }
        }
        this.pageOS.restoreValidationRules();
        this.activeModal.dismiss('cancel');
    }
}
